import React from 'react'
import { useMount } from 'react-use'
import invariant from 'tiny-invariant'

import ErrorBoundaryCard from '@/components/misc/ErrorBoundaryCard'
import ReferralESignatureConsentPdfPreview from '@/components/misc/ReferralESignatureConsentPdfPreview/ReferralESignatureConsentPdfPreview'
import { ServiceFeeDetail } from '@/components/misc/ServiceFeesTable/ServiceFeesTableRows'
import PdfSkeleton from '@/components/skeleton/PdfSkeleton'
import { useCreateReferralESignatureConsentPreviewMutation } from '@/hooks/useCreateReferralESignatureConsentPreviewMutation'
import { CommercialAgreementTypeOption } from '@/redux/slices/commercialAgreementsForm'
import {
  CommercialAgreementStatus,
  ReferralConsentLetterVariant,
  ReferralConsentStage,
  ReferralType,
} from '@/types/misc'
import { CreateReferralESignatureConsentPreviewRequest } from '@/types/requests/referrals'

interface Props {
  fromCompanyId: number
  toCompanyId: number
  clientId?: number
  clientDetails?: CreateReferralESignatureConsentPreviewRequest['clientDetails']
  serviceAreaIds?: number[]
  serviceFees?: ServiceFeeDetail[]
  render?: (props: RenderProps) => React.ReactNode
  agreementType?: CommercialAgreementTypeOption
  agreementStatus: CommercialAgreementStatus
  commissionJustification?: string
  onEditCommissionJustification?: (commissionJustification: string) => void
  referralType?: ReferralType
  referralLinkId?: number
  showAdditionalPdfPages?: boolean
  consentLetterVariant: ReferralConsentLetterVariant
  consentStage: ReferralConsentStage
}

interface RenderProps {
  renderPreview: () => React.ReactNode
  commissionJustification?: string
}

const ReferralESignaturePreview: React.FC<Props> = ({
  fromCompanyId,
  toCompanyId,
  clientId,
  clientDetails,
  serviceAreaIds = [],
  serviceFees,
  render,
  agreementType,
  agreementStatus,
  commissionJustification,
  onEditCommissionJustification,
  referralType,
  referralLinkId,
  showAdditionalPdfPages,
  consentLetterVariant,
  consentStage,
}) => {
  const createLetterPreviewMutation =
    useCreateReferralESignatureConsentPreviewMutation()

  useMount(() => {
    createLetterPreviewMutation.mutate({
      fromCompanyId,
      toCompanyId,
      clientId,
      clientDetails,
      serviceAreaIds,
      type: referralType,
      referralLinkId,
      agreementStatus,
    })
  })

  const eSignatureLetterPreview = createLetterPreviewMutation.data

  if (createLetterPreviewMutation.error) {
    throw new Error('Failed to load preview data')
  }

  if (createLetterPreviewMutation.isPending || !eSignatureLetterPreview) {
    return <PdfSkeleton />
  }

  const consentFiles = showAdditionalPdfPages
    ? eSignatureLetterPreview.eSignatureDetails.commercialAgreement
        ?.consentFiles || []
    : []

  function renderPreview() {
    invariant(eSignatureLetterPreview)

    return (
      <ReferralESignatureConsentPdfPreview
        referral={eSignatureLetterPreview}
        serviceFees={serviceFees}
        agreementType={agreementType}
        commissionJustification={commissionJustification}
        onEditCommissionJustification={onEditCommissionJustification}
        isPreviewLetter
        consentFiles={consentFiles}
        consentLetterVariant={consentLetterVariant}
        consentStage={consentStage}
      />
    )
  }

  return (
    <ErrorBoundaryCard
      message={
        <p>
          We're very sorry but it looks like we're having some technical issues.
          Our team has been notified so please try again later.
        </p>
      }
    >
      {render
        ? render({
            renderPreview,
            commissionJustification,
          })
        : renderPreview()}
    </ErrorBoundaryCard>
  )
}

export default ReferralESignaturePreview
